import { v4 as uuidv4 } from "uuid";

import { useState, useRef, useEffect } from "react";
import { ChatMessage, getAssistantTypeApi, MaintenanceConfig } from "../../api";
import { UserUploadConfig } from "../../pages/chat/types";
import { createErrorMessage, parseToolContent } from "../../utils/helpers";
import { defaultMaintenanceConfig } from "../../utils/maintenance";

export const useChat = () => {
  const [answers, setAnswers] = useState<ChatMessage[]>([]);
  const [questionToSend, setQuestionToSend] = useState<ChatMessage>({
    role: "",
    content: "",
    id: "",
    date: "",
  });
  const [conversationId, setConversationId] = useState<string>(uuidv4());
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  );
  const [isAssistantAPILoading, setIsAssistantAPILoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const [userUploadConfig, setUserUploadConfig] = useState<UserUploadConfig>({
    maxFileSize: 20,
    maxFiles: 5,
  });
  const [maintenanceConfig, setMaintenanceConfig] = useState<MaintenanceConfig>(
    defaultMaintenanceConfig,
  );

  const lastQuestionRef = useRef<string>("");
  const lastAnswerRef = useRef<HTMLDivElement | null>(null);
  const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
  const abortFuncs = useRef([] as AbortController[]);
  const pendingRequestConvIds = useRef<Set<string>>(new Set());
  const pendingQuestionByConvId = useRef<Map<string, string>>(new Map());
  const pendingAsyncAnswersByConvId = useRef<Map<string, ChatMessage[]>>(
    new Map(),
  );

  useEffect(() => {
    const fetchAssistantType = async () => {
      try {
        setIsAssistantAPILoading(true);
        const result = await getAssistantTypeApi();
        if (result) {
          if (result.user_upload_config) {
            setUserUploadConfig({
              maxFiles: result.user_upload_config.max_files,
              maxFileSize: result.user_upload_config.max_file_size,
            });
          }
          if (result.maintenance_config) {
            setMaintenanceConfig(result.maintenance_config);
          }
        }
        setIsAssistantAPILoading(false);
        return result;
      } catch (error) {
        console.error("Error fetching assistant type:", error);
      }
    };
    fetchAssistantType();
  }, []);

  useEffect(() => {
    if (showLoadingMessage) {
      chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      lastAnswerRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [showLoadingMessage]);

  const handleErrorResponse = (
    result: any,
    previousMessages: ChatMessage[],
    newUserMessage: ChatMessage,
    setAnswers: (messages: ChatMessage[]) => void,
  ) => {
    setAnswers([
      ...previousMessages,
      newUserMessage,
      createErrorMessage(result.error),
    ]);
  };

  const handleFinalResult = (
    result: any,
    assistantMessage: ChatMessage,
    previousMessages: ChatMessage[],
    newUserMessage: ChatMessage,
    setAnswers: (messages: ChatMessage[]) => void,
    saveToDB: (messages: ChatMessage[], convId: string) => void,
    conversationId: string,
  ): boolean => {
    const finalMessages = result.choices[0].messages;
    const finalAssistantMessage = finalMessages.find(
      (msg: any) => msg.role === "assistant",
    );
    const finalToolMessage = finalMessages.find(
      (msg: any) => msg.role === "tool",
    );

    if (!finalAssistantMessage) return false;

    // Use the final assistant content as-is, without trying to separate it
    const cleanContent = finalAssistantMessage.content;

    // Create intent message (separate from final answer)
    const messages = [...previousMessages, newUserMessage];

    // Create final assistant message WITH intent stored but not displayed in content
    const finalMessage: ChatMessage = {
      role: "assistant",
      content: cleanContent,
      id: assistantMessage.id,
      date: assistantMessage.date,
      intent: "", // Will be populated below
      citations: [],
    };

    let intentContent = "";
    if (finalToolMessage) {
      const toolContent = parseToolContent(finalToolMessage.content);
      intentContent = toolContent.intent || "";
      finalMessage.citations = toolContent.citations || [];
    } else if (finalAssistantMessage.intent) {
      intentContent = finalAssistantMessage.intent;
    }

    // Store intent in the assistant message (not as separate message)
    finalMessage.intent = intentContent;

    // Add only the assistant message (with intent stored in it)
    messages.push(finalMessage);

    setAnswers(messages);
    setStreamingMessageId(null); // Clear streaming state
    saveToDB(messages, conversationId);
    return true;
  };

  const handleStreamingContent = (
    result: any,
    assistantMessage: ChatMessage,
    streamingContent: { current: string },
    previousMessages: ChatMessage[],
    newUserMessage: ChatMessage,
    setAnswers: (messages: ChatMessage[]) => void,
  ) => {
    streamingContent.current += result.choices[0].delta.content;

    // Update assistant message with streaming content (no separate intent message)
    const updatedAssistantMessage: ChatMessage = {
      ...assistantMessage,
      content: streamingContent.current,
      // Intent should be stored here but not displayed - it comes from the main streaming logic
    };

    // Set this message as currently streaming
    setStreamingMessageId(updatedAssistantMessage.id);

    setAnswers([...previousMessages, newUserMessage, updatedAssistantMessage]);
  };

  const handleCompleteMessages = (
    result: any,
    assistantMessage: ChatMessage,
    previousMessages: ChatMessage[],
    newUserMessage: ChatMessage,
    setAnswers: (messages: ChatMessage[]) => void,
  ) => {
    const messages = result.choices[0].messages;
    const toolMessage = messages.find((msg: any) => msg.role === "tool");

    if (toolMessage) {
      const toolContent = parseToolContent(toolMessage.content);
      const intentContent = toolContent.intent || "";

      assistantMessage.intent = intentContent;
      assistantMessage.citations = toolContent.citations || [];

      // Don't replace assistant content with intent - intent should remain separate
      setAnswers([
        ...previousMessages,
        newUserMessage,
        { ...assistantMessage },
      ]);
    }
  };

  const handleSingleMessage = (
    result: any,
    assistantMessage: ChatMessage,
    previousMessages: ChatMessage[],
    newUserMessage: ChatMessage,
    setAnswers: (messages: ChatMessage[]) => void,
  ) => {
    assistantMessage.content = result.choices[0].message.content;
    setAnswers([...previousMessages, newUserMessage, { ...assistantMessage }]);
  };

  const handleStreamingChunk = (
    result: any,
    assistantMessage: ChatMessage,
    streamingContent: { current: string },
    previousMessages: ChatMessage[],
    newUserMessage: ChatMessage,
    setAnswers: (messages: ChatMessage[]) => void,
    saveToDB: (messages: ChatMessage[], convId: string) => void,
    conversationId: string,
  ): boolean => {
    if (result.error) {
      handleErrorResponse(result, previousMessages, newUserMessage, setAnswers);
      return false;
    }

    const isFinalResult = result.model || result.choices?.[0]?.finish_reason;
    if (isFinalResult && result.choices?.[0]?.messages) {
      return handleFinalResult(
        result,
        assistantMessage,
        previousMessages,
        newUserMessage,
        setAnswers,
        saveToDB,
        conversationId,
      );
    }

    if (result.choices?.[0]) {
      if (result.choices[0].delta?.content) {
        handleStreamingContent(
          result,
          assistantMessage,
          streamingContent,
          previousMessages,
          newUserMessage,
          setAnswers,
        );
      } else if (result.choices[0].messages) {
        handleCompleteMessages(
          result,
          assistantMessage,
          previousMessages,
          newUserMessage,
          setAnswers,
        );
      } else if (result.choices[0].message) {
        handleSingleMessage(
          result,
          assistantMessage,
          previousMessages,
          newUserMessage,
          setAnswers,
        );
      }
    }

    return false;
  };

  const resetQuestionToSend = () => {
    setQuestionToSend({
      role: "",
      content: "",
      id: "",
      date: "",
    });
  };

  const handleQuestionToSend = (question: string) => {
    setQuestionToSend({ ...questionToSend, content: question });
  };

  const onStopGenerating = () => {
    abortFuncs.current.forEach((a) => a.abort());
    setShowLoadingMessage(false);
    setIsLoading(false);
  };

  const resetLoadingState = () => {
    setShowLoadingMessage(false);
    setIsLoading(false);
  };

  const addPendingRequest = (convId: string, question: string) => {
    pendingRequestConvIds.current.add(convId);
    pendingQuestionByConvId.current.set(convId, question);
  };

  const removePendingRequest = (convId: string) => {
    pendingRequestConvIds.current.delete(convId);
    pendingQuestionByConvId.current.delete(convId);
    pendingAsyncAnswersByConvId.current.delete(convId);
  };

  const setPendingAsyncAnswers = (convId: string, msgs: ChatMessage[]) => {
    pendingAsyncAnswersByConvId.current.set(convId, msgs);
  };

  const getPendingAsyncAnswers = (
    convId: string,
  ): ChatMessage[] | undefined => {
    return pendingAsyncAnswersByConvId.current.get(convId);
  };

  const restorePendingState = (convId: string) => {
    if (!pendingRequestConvIds.current.has(convId)) return;
    setIsLoading(true);
    const asyncAnswers = pendingAsyncAnswersByConvId.current.get(convId);
    if (asyncAnswers) {
      setAnswers(asyncAnswers);
    } else {
      setShowLoadingMessage(true);
      lastQuestionRef.current =
        pendingQuestionByConvId.current.get(convId) || "";
    }
  };

  return {
    abortFuncs,
    answers,
    chatMessageStreamEnd,
    conversationId,
    isAssistantAPILoading,
    isLoading,
    lastAnswerRef,
    lastQuestionRef,
    questionToSend,
    showLoadingMessage,
    streamingMessageId,
    userUploadConfig,
    maintenanceConfig,
    handleQuestionToSend,
    handleStreamingChunk,
    onStopGenerating,
    resetLoadingState,
    resetQuestionToSend,
    addPendingRequest,
    removePendingRequest,
    setPendingAsyncAnswers,
    getPendingAsyncAnswers,
    restorePendingState,
    setAnswers,
    setConversationId,
    setIsLoading,
    setQuestionToSend,
    setShowLoadingMessage,
    setStreamingMessageId,
  };
};
